import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../css/booking.css';
import { createBooking as createBookingAPI, updateBooking as updateBookingAPI, getBookingById } from '../../api/booking';
import { getServiceById } from '../../api/services';
import Select, { components } from 'react-select';
import hiveroomsBooking from '../../assets/icons/icon_hiverooms_booking.png';
import { getNames } from 'country-list';
import PaymentModal from '../../components/PaymentModal';
import TermsModal from '../../components/TermsModal';

const countryOptions = getNames().map(country => ({
  value: country,
  label: country
}));

// List of experience service IDs
const experienceServices = [
  'moalboal-kawasan',
  'oslob-simala',
  'cebu-city-tours',
  'team-building',
  'kayak-sup',
  'paddle-journey',
  'kids-on-water',
  'freediving-camp'
];

// Function to check if a service is an experience service
const isExperienceService = (serviceId) => {
  return experienceServices.includes(serviceId);
};

// Function to check if a booking is for an experience service (for edit mode)
const isExperienceBooking = (serviceTitle, selectedService = null) => {
  // If we have selectedService data, use its category (most reliable)
  if (selectedService && selectedService.category) {
    return selectedService.category === 'experience' || selectedService.category === 'event';
  }

  // Fallback to hardcoded titles for legacy services
  const experienceServiceTitles = [
    'Moalboal + Kawasan Tours',
    'Oslob + Simala Tours',
    'Cebu City Tours',
    'Team Building Package',
    'Kayak & Stand up Paddling',
    'Paddle Journey',
    'Kids on Water',
    'Freediving and Camp x Dive',
    'Fisherman for a Day',
    'Peborit ni Denver'  // Custom experience service
  ];
  return experienceServiceTitles.includes(serviceTitle);
};


// Function to get experience service price from title (for edit mode)
const getExperienceServicePrice = (serviceTitle) => {
  const experienceServicePrices = {
    'Moalboal + Kawasan Tours': 1689,
    'Oslob + Simala Tours': 1850,
    'Cebu City Tours': 1200,
    'Team Building Package': 2500,
    'Kayak & Stand up Paddling': 800,
    'Paddle Journey': 950,
    'Kids on Water': 650,
    'Freediving and Camp x Dive': 3200,
    'Fisherman for a Day': 1500,
    'Peborit ni Denver': 2000  // Custom experience service - adjust price as needed
  };
  return experienceServicePrices[serviceTitle] || 0;
};

const genderOptions = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const ageOptions = Array.from({ length: 100 }, (_, i) => ({ value: (i + 1).toString(), label: (i + 1).toString() }));
const contactAgeOptions = Array.from({ length: 83 }, (_, i) => ({ value: (i + 18).toString(), label: (i + 18).toString() }));


// Generate unique booking ID: NAME-DDMMYY-XXXXXX (with timestamp for uniqueness)
const generateBookingId = (name, date) => {
  // Clean name: remove spaces, special chars, take first 3-4 letters, uppercase
  const cleanName = name.replace(/[^a-zA-Z]/g, '').toUpperCase().substring(0, 4).padEnd(4, 'X');

  // Format date as DDMMYY
  const bookingDate = new Date(date);
  const day = bookingDate.getDate().toString().padStart(2, '0');
  const month = (bookingDate.getMonth() + 1).toString().padStart(2, '0');
  const year = bookingDate.getFullYear().toString().slice(-2);
  const dateStr = `${day}${month}${year}`;

  // Use timestamp for guaranteed uniqueness (last 6 digits)
  const timestamp = Date.now().toString().slice(-6);

  return `${cleanName}-${dateStr}-${timestamp}`;
};

// Format date for user-friendly display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  return date.toLocaleDateString('en-US', options);
};

export default function App({ editMode = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Get service data from location.state or URL query parameters
  const getServiceDataFromUrl = () => {
    if (location.state) {
      return location.state;
    }

    // If no state data, check URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const serviceId = searchParams.get('serviceId');
    const title = searchParams.get('title');

    if (serviceId && title) {
      return {
        selectedServiceId: serviceId,
        serviceTitle: decodeURIComponent(title)
      };
    }

    return null;
  };

  const serviceData = getServiceDataFromUrl();

  // State for booking data when editing
  const [editBookingData, setEditBookingData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Check if we're in edit mode from props or URL
  const isEditMode = editMode || serviceData?.editMode || false;
  const bookingIdFromURL = params.bookingId; // This will be the human-readable bookingId like "TEST-010125-123456"

  // Two types of edit modes:
  // 1. Internal edit (from admin/management) - uses serviceData.existingBooking
  // 2. URL-based edit (from email link) - uses editBookingData fetched from API
  const existingBooking = serviceData?.existingBooking || editBookingData || null;


  // Simplified: No need for complex helper functions anymore
  // Just use selectedService.options?.length > 0 to determine if service has options

  // State for fetched service data
  const [selectedService, setSelectedService] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Add class to body on mount
    document.body.classList.add('booking-page-bg');

    // Remove class on unmount
    return () => {
      document.body.classList.remove('booking-page-bg');
    };
  }, []);

  // Fetch booking data for edit mode when accessed via URL
  useEffect(() => {
    const fetchBookingData = async () => {
      if (editMode && bookingIdFromURL && !serviceData?.existingBooking) {
        setEditLoading(true);
        try {
          const response = await getBookingById(bookingIdFromURL);

          // Format the data to match the structure expected by existing edit mode
          const formattedBookingData = {
            data: response.data, // The booking data is in response.data
            category: response.data?.category
          };
          setEditBookingData(formattedBookingData);
        } catch (error) {
          console.error('Error fetching booking data:', error);
          alert('Unable to load booking data for editing. Please try again later.');
          navigate('/'); // Redirect to main page if booking not found
        } finally {
          setEditLoading(false);
        }
      }
    };

    fetchBookingData();
  }, [editMode, bookingIdFromURL, serviceData?.existingBooking, navigate]);

  // Fetch service data if service ID is provided
  useEffect(() => {
    const fetchServiceData = async () => {
      // For new bookings, use serviceData.selectedServiceId
      // For edit mode, use existingBooking.data.serviceId
      const serviceIdToFetch = isEditMode
        ? existingBooking?.data?.serviceId
        : serviceData?.selectedServiceId;

      if (serviceIdToFetch) {
        try {
          setLoading(true);
          const response = await getServiceById(serviceIdToFetch);
          if (response.success) {
            setSelectedService(response.data);
          }
        } catch (error) {
          console.error('Error fetching service:', error);
          setError('Failed to load service details');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchServiceData();
  }, [serviceData?.selectedServiceId, isEditMode, existingBooking?.data?.serviceId]);

  // Update boats when service data is loaded
  useEffect(() => {
    if (selectedService) {
      const newBoats = getBoatsForService();
      setBoats(newBoats);

      // In edit mode, try to find the originally booked option
      if (newBoats && newBoats.length > 0) {
        if (isEditMode && existingBooking) {
          let originalOption = null;

          // First try to match by selectedOption ID (for stay services with options)
          if (existingBooking.data?.selectedOption) {
            originalOption = newBoats.find(boat => boat.id === existingBooking.data.selectedOption);
          }

          // Fallback to matching by boat name (for legacy bookings or experience services)
          if (!originalOption && existingBooking.data?.boat) {
            originalOption = newBoats.find(boat => boat.name === existingBooking.data.boat);
          }

          const finalSelection = originalOption || newBoats[0];
          setSelectedBoat(finalSelection);
        } else {
          setSelectedBoat(newBoats[0]);
        }
      }
    }
  }, [selectedService]);


  // Get the current service title
  const getCurrentServiceTitle = () => {
    // If we have API service data, use it (priority)
    if (selectedService) {
      return selectedService.title;
    }

    // In edit mode, use the boat name as fallback
    if (isEditMode && existingBooking) {
      return existingBooking.data?.boat;
    }

    // For new bookings, use service data title
    return serviceData?.serviceTitle;
  };

  // Get boats/options for the selected service
  const getBoatsForService = () => {
    // For new API-based services (prioritize this even in edit mode)
    if (selectedService) {
      if (selectedService.options && selectedService.options.length > 0) {
        // Convert service options to boat-like objects for compatibility (both stay and experience)
        return selectedService.options.map(option => ({
          id: option._id,
          name: option.name,
          description: option.description,
          price: option.price,
          slots: option.slots,
          features: option.features || []
        }));
      } else if (selectedService.category === 'experience' || selectedService.category === 'event') {
        // For experience/event services without options, create a single "boat" object representing the package
        return [{
          id: selectedService._id,
          name: selectedService.title,
          description: selectedService.description,
          price: selectedService.price,
          slots: selectedService.minPax || 1,
          features: selectedService.features || []
        }];
      }
    }

    // Fallback: return empty array if no service data available
    return [];
  };

  const [boats, setBoats] = useState(getBoatsForService());

  // Initialize selected boat - from existing booking or first boat, or default for experience services
  const getInitialBoat = () => {
    if (isEditMode && existingBooking) {
      // First try to find in selectedService options using selectedOption ID
      if (selectedService && selectedService.options && existingBooking.data?.selectedOption) {
        const foundOption = selectedService.options.find(option => option._id === existingBooking.data.selectedOption);
        if (foundOption) {
          return {
            id: foundOption._id,
            name: foundOption.name,
            description: foundOption.description,
            price: foundOption.price,
            slots: foundOption.slots,
            features: foundOption.features || []
          };
        }
      }

      // Fallback: try to match by boat name (for legacy bookings)
      if (selectedService && selectedService.options) {
        const foundOption = selectedService.options.find(option => option.name === existingBooking.data?.boat);
        if (foundOption) {
          return {
            id: foundOption._id,
            name: foundOption.name,
            description: foundOption.description,
            price: foundOption.price,
            slots: foundOption.slots,
            features: foundOption.features || []
          };
        }
      }

      // If no selectedService data, return null (will be set when service loads)
      return null;
    }

    // For services without options, don't create a default boat object
    // The pricing and service info will be handled by the service data directly

    // For new API services, wait for service data to load
    if (serviceData?.selectedServiceId && !selectedService) {
      return null; // Will be set when service loads
    }

    return boats[0] || null;
  };

  const [selectedBoat, setSelectedBoat] = useState(getInitialBoat());

  // Initialize passengers from existing booking or empty
  const getInitialPassengers = () => {
    if (isEditMode && existingBooking) {
      return existingBooking.data?.passengers || [{ name: '', gender: '', country: '', age: '' }];
    }
    return [{ name: '', gender: '', country: '', age: '' }];
  };

  const [passengers, setPassengers] = useState(getInitialPassengers());

  // Initialize form data from existing booking or empty
  const getInitialFormData = () => {
    if (isEditMode && existingBooking) {
      return {
        name: existingBooking.data?.name || '',
        email: existingBooking.data?.email || '',
        phone: existingBooking.data?.phone || '',
        date: existingBooking.data?.date || '',
        age: existingBooking.data?.age || '',
        country: existingBooking.data?.country || '',
        gender: existingBooking.data?.gender || '',
        termsAccepted: true, // Auto-accept for edits
      };
    }
    return {
      name: '',
      email: '',
      phone: '',
      date: '',
      age: '',
      country: '',
      gender: '',
      termsAccepted: false,
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());

  // Reinitialize form data when editBookingData becomes available (URL-based edit mode)
  useEffect(() => {
    if (isEditMode && editBookingData && !serviceData?.existingBooking) {
      const newFormData = getInitialFormData();
      setFormData(newFormData);

      // Also reinitialize passengers
      const newPassengers = getInitialPassengers();
      setPassengers(newPassengers);

      // And reinitialize booking ID
      const newBookingId = editBookingData.data?.bookingId || '';
      setBookingId(newBookingId);
    }
  }, [editBookingData, isEditMode, serviceData?.existingBooking]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingCompleted, setBookingCompleted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});
  const [copyFeedback, setCopyFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isBookingUpdated, setIsBookingUpdated] = useState(false);
  const [isViewingPrintFromModal, setIsViewingPrintFromModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Store original booking ID for updates
  const [bookingId, setBookingId] = useState(isEditMode ? existingBooking?.data?.bookingId || '' : '');

  useEffect(() => {
    // Only set tomorrow's date if not in edit mode and no date is set
    if (!isEditMode) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];
      setFormData((prev) => ({
        ...prev,
        date: prev.date || dateStr // Only set if no date exists
      }));
    }
  }, [isEditMode]); // Remove formData.date from dependencies

  const handlePassengerChange = (i, field, value) => {
    const newPassengers = [...passengers];
    newPassengers[i][field] = value;
    setPassengers(newPassengers);
  };

  const handleAddPassenger = () => {
    // Check if booking has a selected option (either from editing existing or selecting new)
    const hasSelectedOption = selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption);

    if (hasSelectedOption) {
      // Booking has selected option - use option's passenger limit
      const maxPax = selectedBoat?.slots || 1;
      
      // If max pax is 1, contact person fills it completely - no additional passengers allowed
      if (maxPax === 1) {
        setError(`This service allows only 1 passenger total (contact person only).`);
        return;
      }
      
      if (passengers.length < maxPax - 1) {
        setPassengers([...passengers, { name: '', gender: '', country: '', age: '' }]);
      } else {
        setError(`Maximum of ${maxPax} passengers allowed for this option (contact person counts as passenger #1).`);
      }
    } else {
      // Booking has no selected option - use service-level passenger limits
      const maxPassengers = selectedService?.minPax || 30;
      
      // If max pax is 1, contact person fills it completely - no additional passengers allowed
      if (maxPassengers === 1) {
        setError(`This service allows only 1 passenger total (contact person only).`);
        return;
      }
      
      if (passengers.length < maxPassengers - 1) { // -1 because contact person is separate
        setPassengers([...passengers, { name: '', gender: '', country: '', age: '' }]);
      } else {
        setError(`Maximum of ${maxPassengers} total passengers allowed for this service (including contact person).`);
      }
    }
  };

  const handleRemovePassenger = (index) => {
    // Check if service only allows 1 passenger total
    const maxPax = selectedBoat?.slots || selectedService?.minPax || 30;
    
    // If service allows only 1 passenger, contact person is sufficient - no validation needed
    if (maxPax === 1) {
      const newPassengers = passengers.filter((_, i) => i !== index);
      setPassengers(newPassengers);
      setError('');
      return;
    }
    
    // For multi-passenger services, ensure at least one additional passenger
    if (passengers.length <= 1) {
      setError('At least one passenger is required.');
      return;
    }
    const newPassengers = passengers.filter((_, i) => i !== index);
    setPassengers(newPassengers);
    setError('');
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  // Validation function for payment (without terms check)
  const validateForPayment = () => {
    setError('');
    const newTouched = { name: true, email: true, phone: true, age: true, country: true, gender: true };
    passengers.forEach((_, i) => {
      newTouched[`passenger_name_${i}`] = true;
      newTouched[`passenger_gender_${i}`] = true;
      newTouched[`passenger_country_${i}`] = true;
      newTouched[`passenger_age_${i}`] = true;
    });
    setTouched(newTouched);

    if (!formData.name || !formData.email || !formData.phone || !formData.age || !formData.country || !formData.gender) {
      setError('Please fill in all contact fields.');
      return false;
    }

    if (!formData.date) {
      setError('Please select a booking date.');
      return false;
    }

    // Check 3-day advance booking rule
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    const diffTime = selectedDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 3) {
      setError('Bookings must be made at least 3 days in advance. Please select a date that is at least 3 days from today.');
      return false;
    }

    // Only require boat selection for services with options
    if (!selectedBoat && selectedService?.options && selectedService.options.length > 0) {
      setError('Please select an option.');
      return false;
    }
    const filteredPassengers = passengers.filter(
      (p) => p.name.trim() && p.gender && p.country && p.age
    );

    // Contact person counts as passenger #1, so additional passengers are optional
    // Only validate if there are partially filled passenger entries
    const hasPartialPassengerInfo = passengers.some(p =>
      p.name.trim() || p.gender || p.country || p.age
    );

    if (hasPartialPassengerInfo && filteredPassengers.length !== passengers.length) {
      setError('Please complete all passenger information or remove empty passenger entries.');
      return false;
    }

    // Check minimum pax requirement
    const totalPassengers = filteredPassengers.length + 1; // +1 for contact person
    const minPax = selectedBoat?.slots || selectedService?.minPax;
    
    // If minPax is explicitly set and is 1, contact person alone satisfies the requirement
    if (minPax === 1) {
      return true; // Contact person alone satisfies the requirement
    }

    // If minPax is defined and greater than 1, enforce the minimum
    if (minPax && minPax > 1 && totalPassengers < minPax) {
      setError(`Minimum ${minPax} passengers required for this service. You currently have ${totalPassengers} passenger${totalPassengers === 1 ? '' : 's'}. Please add ${minPax - totalPassengers} more passenger${minPax - totalPassengers === 1 ? '' : 's'}.`);
      return false;
    }

    // If no minPax is defined, allow any number (including just contact person)
    // This handles services without minimum requirements

    return true;
  };

  // Validation function (extracted from handleBook)
  const validateBookingData = () => {
    setError('');
    const newTouched = { name: true, email: true, phone: true, age: true, country: true, gender: true };
    passengers.forEach((_, i) => {
      newTouched[`passenger_name_${i}`] = true;
      newTouched[`passenger_gender_${i}`] = true;
      newTouched[`passenger_country_${i}`] = true;
      newTouched[`passenger_age_${i}`] = true;
    });
    setTouched(newTouched);

    if (!formData.name || !formData.email || !formData.phone || !formData.age || !formData.country || !formData.gender) {
      setError('Please fill in all contact fields.');
      return false;
    }

    if (!formData.termsAccepted) {
      setError('Please accept the Terms and Conditions to proceed with your booking.');
      return false;
    }
    if (!formData.date) {
      setError('Please select a booking date.');
      return false;
    }

    // Check 3-day advance booking rule
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    const diffTime = selectedDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 3) {
      setError('Bookings must be made at least 3 days in advance. Please select a date that is at least 3 days from today.');
      return false;
    }

    // Only require boat selection for services with options
    if (!selectedBoat && selectedService?.options && selectedService.options.length > 0) {
      setError('Please select an option.');
      return false;
    }
    const filteredPassengers = passengers.filter(
      (p) => p.name.trim() && p.gender && p.country && p.age
    );

    // Contact person counts as passenger #1, so additional passengers are optional
    // Only validate if there are partially filled passenger entries
    const hasPartialPassengerInfo = passengers.some(p =>
      p.name.trim() || p.gender || p.country || p.age
    );

    if (hasPartialPassengerInfo && filteredPassengers.length !== passengers.length) {
      setError('Please fill in all passenger fields.');
      return false;
    }

    // Check minimum pax requirement
    const totalPassengers = filteredPassengers.length + 1; // +1 for contact person
    const minPax = selectedBoat?.slots || selectedService?.minPax;
    
    // If minPax is explicitly set and is 1, contact person alone satisfies the requirement
    if (minPax === 1) {
      return true; // Contact person alone satisfies the requirement
    }

    // If minPax is defined and greater than 1, enforce the minimum
    if (minPax && minPax > 1 && totalPassengers < minPax) {
      setError(`Minimum ${minPax} passengers required for this service. You currently have ${totalPassengers} passenger${totalPassengers === 1 ? '' : 's'}. Please add ${minPax - totalPassengers} more passenger${minPax - totalPassengers === 1 ? '' : 's'}.`);
      return false;
    }

    // If no minPax is defined, allow any number (including just contact person)
    // This handles services without minimum requirements

    return true;
  };

  const handleBook = async () => {
    if (isEditMode) {
      // For edit mode, just validate and show modal
      if (validateBookingData()) {
        setShowSuccessModal(true);
      }
    } else {
      // For new bookings, do the full process
      setIsLoading(true);
      if (!validateBookingData()) {
        setIsLoading(false);
        return;
      }

      try {
        const newBookingId = generateBookingId(formData.name, formData.date);
        setBookingId(newBookingId);

        const filteredPassengers = passengers.filter(
          (p) => p.name.trim() && p.gender && p.country && p.age
        );

        // Determine category
        const isExperience = isExperienceService(serviceData?.selectedServiceId) ||
          (isEditMode && (existingBooking?.data?.category === 'experience' || selectedService?.category === 'experience' || isExperienceBooking(getCurrentServiceTitle())));

        const bookingData = {
          bookingId: newBookingId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          age: formData.age,
          country: formData.country,
          gender: formData.gender,
          date: formData.date,
          boat: selectedBoat?.name || (getCurrentServiceTitle() || selectedService?.title || 'Service Package'),
          selectedOption: selectedBoat?.id || undefined,
          serviceId: selectedService?._id,
          category: existingBooking?.data?.category || selectedService?.category || (isExperience ? 'experience' : 'stay'),
          totalAmount: getTotalPrice(),
          operationalStartTime: selectedService?.operationalStartTime,
          bookingDuration: selectedService?.bookingDuration,
          passengers: filteredPassengers.map(p => ({
            name: p.name,
            gender: p.gender,
            country: p.country,
            age: p.age,
          })),
        };

        // Create the booking directly
        const response = await createBookingAPI(bookingData);

        // Handle both old format (direct booking object) and new format (with success field)
        const isSuccess = response.success === true || (response.success === undefined && response.bookingId);

        if (isSuccess) {
          setBookingCompleted(true);
          setShowConfirmation(true);
          setError('');
        } else {
          console.error('Booking creation failed:', response);
          setError(response.message || response.error || 'Failed to create booking');
        }
        setIsLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to create booking');
        setIsLoading(false);
      }
    }
  };

  // New function to actually confirm the update
  const handleConfirmUpdate = async () => {
    setIsLoading(true);
    try {
      const filteredPassengers = passengers.filter(
        (p) => p.name.trim() && p.gender && p.country && p.age
      );

      // Determine category
      const isExperience = isExperienceService(serviceData?.selectedServiceId) ||
        (isEditMode && (existingBooking?.data?.category === 'experience' || selectedService?.category === 'experience' || isExperienceBooking(getCurrentServiceTitle())));

      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: formData.age,
        country: formData.country,
        gender: formData.gender,
        date: formData.date,
        boat: selectedBoat?.name || (getCurrentServiceTitle() || selectedService?.title || 'Service Package'),
        selectedOption: selectedBoat?.id || undefined,
        serviceId: selectedService?._id,
        category: existingBooking?.data?.category || selectedService?.category || (isExperience ? 'experience' : 'stay'),
        totalAmount: getTotalPrice(),
        passengers: filteredPassengers.map(p => ({
          name: p.name,
          gender: p.gender,
          country: p.country,
          age: p.age,
        })),
      };


      if (!bookingId) {
        throw new Error('No booking ID found for update. Cannot proceed with update.');
      }

      const response = await updateBookingAPI(bookingId, bookingData);

      // Handle both old format (direct booking object) and new format (with success field)
      const isSuccess = response.success === true || (response.success === undefined && response.bookingId);

      if (isSuccess) {
        setIsBookingUpdated(true);
        setError('');
      } else {
        console.error('Booking update failed:', response);
        setError(response.message || response.error || 'Failed to update booking');
      }
      setIsLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to update booking');
      setIsLoading(false);
    }
  };

  // Payment success handler
  const handlePaymentSuccess = async (paymentData) => {
    try {
      console.log('Payment successful:', paymentData);
      setPaymentCompleted(true);
      setPaymentFailed(false);
      setPaymentError('');
      setShowPaymentModal(false);
      setPendingBookingData(null);
      
      // Show success message
      setError(''); // Clear any previous errors
      console.log('Payment completed successfully! You can now confirm your booking.');

    } catch (err) {
      console.error('Error handling payment success:', err);
      setError('Payment processed but there was an error. Please try again.');
      setShowPaymentModal(false);
    }
  };

  // Payment failure handler
  const handlePaymentFailure = async (paymentData) => {
    try {
      console.log('Payment failed:', paymentData);
      setPaymentCompleted(false);
      setPaymentFailed(true);
      setPaymentError(paymentData?.error || 'Payment processing failed');
      setShowPaymentModal(false);
      setPendingBookingData(null);
      
      // Show error message
      setError(`Payment failed: ${paymentData?.error || 'Please try again or contact support.'}`);

    } catch (err) {
      console.error('Error handling payment failure:', err);
      setError('There was an error processing the payment failure. Please try again.');
      setShowPaymentModal(false);
    }
  };

  // Payment error handler
  const handlePaymentError = (errorMessage) => {
    console.error('Payment error:', errorMessage);
    setError(errorMessage || 'Payment failed. Please try again.');
    setShowPaymentModal(false);
    setPendingBookingData(null);
  };

  // Close payment modal handler
  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPendingBookingData(null);
  };


  const getTotalPrice = () => {
    // For API-based services
    if (selectedService) {
      if (selectedService.options && selectedService.options.length > 0 && selectedBoat) {
        // Service has options - use selected option price
        if (selectedService.category === 'experience') {
          const totalPassengers = passengers.filter(p => p.name.trim()).length + 1; // +1 for contact person
          return selectedBoat.price * totalPassengers;
        } else {
          // For stay services with options, make pricing per-person like experience services
          const totalPassengers = passengers.filter(p => p.name.trim()).length + 1; // +1 for contact person
          return selectedBoat.price * totalPassengers;
        }
      } else if (selectedService.category === 'experience') {
        // Experience service without options - use service base price
        const totalPassengers = passengers.filter(p => p.name.trim()).length + 1; // +1 for contact person
        return selectedService.price * totalPassengers;
      } else if (selectedService.category === 'stay') {
        // For stay services, use selectedBoat price if available, otherwise use service base price
        return selectedBoat?.price || selectedService.price || 0;
      }
    }

    // For Experience services (legacy), get price from service data
    if (isExperienceService(serviceData?.selectedServiceId)) {
      // Experience service pricing - per person rates
      const experienceServicePrices = {
        'moalboal-kawasan': 1689,
        'oslob-simala': 1850,
        'cebu-city-tours': 1200,
        'team-building': 2500,
        'kayak-sup': 800,
        'paddle-journey': 950,
        'kids-on-water': 650,
        'freediving-camp': 3200
      };
      const totalPassengers = passengers.filter(p => p.name.trim()).length + 1; // +1 for contact person
      return (experienceServicePrices[serviceData?.selectedServiceId] || 0) * totalPassengers;
    }

    // For Experience services in edit mode, get price from service title
    if (isEditMode && (existingBooking?.category === 'experience' || selectedService?.category === 'experience' || isExperienceBooking(getCurrentServiceTitle()))) {
      const totalPassengers = passengers.filter(p => p.name.trim()).length + 1; // +1 for contact person
      return getExperienceServicePrice(getCurrentServiceTitle()) * totalPassengers;
    }

    // For Experience services, price is based on passenger count
    if (selectedService?.category === 'experience') {
      const totalPassengers = passengers.filter(p => p.name.trim()).length + 1; // +1 for contact person
      if (selectedService.options?.length > 0 && selectedBoat) {
        return selectedBoat.price * totalPassengers;
      } else {
        return selectedService.price * totalPassengers;
      }
    }
    // Fallback: try selectedBoat price, then selectedService price, then 0
    return selectedBoat?.price || selectedService?.price || 0;
  };

  const handleNewBooking = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setShowConfirmation(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      age: '',
      country: '',
      gender: '',
    });
    setPassengers([{ name: '', gender: '', country: '', age: '' }]);
    setTouched({});
    setError('');
    setBookingId('');
    setBookingCompleted(false);
    
    // Reset payment status
    setPaymentCompleted(false);
    setPaymentFailed(false);
    setPaymentError('');
  };

  // Navigation handlers for success modal (with auto-logout)
  const handleViewDetails = () => {
    if (showSuccessModal) {
      // If called from modal, stay in modal but show print view
      setIsViewingPrintFromModal(true);
      setShowSuccessModal(false);
      setShowConfirmation(true);
    } else {
      // If called from elsewhere, just show confirmation
      setShowConfirmation(true);
    }
  };

  const handleBackToModal = () => {
    setShowConfirmation(false);
    setShowSuccessModal(true);
    setIsViewingPrintFromModal(false);
  };

  const handleMakeMoreChanges = () => {
    setShowSuccessModal(false);
    setIsBookingUpdated(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReturnHome = () => {
    setShowSuccessModal(false);
    navigate('/', { replace: true });
  };

  const handleLogout = () => {
    navigate('/', { replace: true });
  };

  const handleCopyBookingId = async () => {
    try {
      await navigator.clipboard.writeText(bookingId);
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = bookingId;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(''), 2000);
      } catch (fallbackErr) {
        setCopyFeedback('Copy failed');
        setTimeout(() => setCopyFeedback(''), 2000);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="container">
      <header className="booking-header">
        <div className="header-nav">
          <Link to="/" className="nav-link">
            <i className="fas fa-arrow-left"></i>
            <span>Back to Services</span>
          </Link>
          {isEditMode && (
            <button className="logout-btn" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          )}
        </div>

        <div className="header-content">
          <div className="header-main">
            <h1>
              <img src={hiveroomsBooking} alt="Hiverooms Booking" />
              {isEditMode ? 'Edit Your Booking' : 'Complete Your Booking'}
            </h1>
            <p className="header-subtitle">
              {isEditMode
                ? 'Update your reservation details below'
                : 'Fill out the details below'
              }
            </p>
            {isEditMode && (
              <div className="edit-mode-badge">
                <i className="fas fa-edit"></i>
                <span>Editing Booking ID: {bookingId}</span>
              </div>
            )}
          </div>

          {getCurrentServiceTitle() && (
            <div className="service-summary-card">
              <div className="service-summary-header">
                <h3>{getCurrentServiceTitle()}</h3>
                <div className="service-price">
                  <span className="price-label">Current Selection</span>
                  <span className="price-value">
                    PHP{(selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption))
                      ? (selectedBoat?.price || 0)
                      : (selectedService?.price || 0)}
                    {(selectedService?.category === 'experience' || selectedService?.category === 'event' ||
                      (isEditMode && (existingBooking?.data?.category === 'experience' || existingBooking?.data?.category === 'event' || isExperienceBooking(getCurrentServiceTitle()))) ||
                      isExperienceService(serviceData?.selectedServiceId)) &&
                      '/person'
                    }
                  </span>
                </div>
              </div>
              <div className="service-summary-details">
                {!(isExperienceService(serviceData?.selectedServiceId) ||
                  (isEditMode && (existingBooking?.category === 'experience' || existingBooking?.category === 'event' || selectedService?.category === 'experience' || selectedService?.category === 'event' || isExperienceBooking(getCurrentServiceTitle())))) && (
                    <div className="summary-item">
                      <i className="fas fa-ship"></i>
                      <span>{selectedBoat?.name || 'Loading...'}</span>
                    </div>
                  )}
                <div className="summary-item">
                  <i className="fas fa-users"></i>
                  <span>
                    {(isExperienceService(serviceData?.selectedServiceId) ||
                      (isEditMode && (existingBooking?.category === 'experience' || existingBooking?.category === 'event' || selectedService?.category === 'experience' || selectedService?.category === 'event' || isExperienceBooking(getCurrentServiceTitle()))))
                      ? 'Group experience package'
                      : (serviceData?.selectedServiceId === 'fisherman-for-a-day' ||
                        (isEditMode && getCurrentServiceTitle() === 'Fisherman for a Day'))
                        ? '20-25 passengers'
                        : `Up to ${selectedBoat?.slots || 0} passengers`}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className={`app-container ${selectedService?.category === 'experience' ? 'fisherman-single-column' : ''}`}>
        <div className="booking-form">
          <div className="form-section contact-section">
            <div className="section-header">
              <div className="section-icon">
                <i className="fas fa-user"></i>
              </div>
              <div className="section-title">
                <h2>Contact Information</h2>
                <p>We'll use this information to confirm your booking</p>
              </div>
            </div>
            {['name', 'email', 'phone'].map((field) => (
              <div className="form-group" key={field}>
                <label htmlFor={field}>{field[0].toUpperCase() + field.slice(1)}</label>
                <input
                  type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                  id={field}
                  value={formData[field]}
                  onChange={(e) => {
                    if (field === 'phone') {
                      const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                      setFormData({ ...formData, [field]: onlyNums });
                    } else if (field === 'email') {
                      setFormData({ ...formData, [field]: e.target.value });
                    } else {
                      setFormData({ ...formData, [field]: e.target.value });
                    }
                  }
                  }
                  onBlur={() => handleBlur(field)}
                  className={touched[field] && !formData[field] ? 'input-error' : ''}
                  inputMode={field === 'phone' ? 'numeric' : undefined}
                  pattern={field === 'phone' ? '[0-9]*' : undefined}
                />
                {touched[field] && !formData[field] && (
                  <div className="field-error"><i className="fas fa-times"></i> This field is required.</div>
                )}
              </div>
            ))}

            <div className="form-group">
              <label htmlFor="age">Age</label>
              <Select
                classNamePrefix="react-select"
                options={contactAgeOptions}
                value={contactAgeOptions.find(opt => opt.value === formData.age) || null}
                onChange={opt => setFormData({ ...formData, age: opt ? opt.value : '' })}
                placeholder="Select Age (18+)"
                onBlur={() => handleBlur('age')}
                className={touched.age && !formData.age ? 'input-error' : ''}
                isSearchable
                components={{
                  Input: (props) => (
                    <components.Input
                      {...props}
                      inputMode="numeric"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                          e.preventDefault();
                        }
                      }}
                    />
                  )
                }}
              />
              {touched.age && !formData.age && (
                <div className="field-error"><i className="fas fa-times"></i> This field is required.</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="country">Country</label>
              <Select
                classNamePrefix="react-select"
                options={countryOptions}
                value={countryOptions.find(opt => opt.value === formData.country) || null}
                onChange={opt => setFormData({ ...formData, country: opt ? opt.value : '' })}
                placeholder="Select Country"
                isSearchable
                onBlur={() => handleBlur('country')}
                className={touched.country && !formData.country ? 'input-error' : ''}
                components={{
                  Input: (props) => (
                    <components.Input
                      {...props}
                      onKeyPress={(e) => {
                        if (/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  )
                }}
              />
              {touched.country && !formData.country && (
                <div className="field-error"><i className="fas fa-times"></i> This field is required.</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <Select
                classNamePrefix="react-select"
                options={genderOptions}
                value={genderOptions.find(opt => opt.value === formData.gender) || null}
                onChange={opt => setFormData({ ...formData, gender: opt ? opt.value : '' })}
                placeholder="Select Gender"
                onBlur={() => handleBlur('gender')}
                className={touched.gender && !formData.gender ? 'input-error' : ''}
                isSearchable
                components={{
                  Input: (props) => (
                    <components.Input
                      {...props}
                      onKeyPress={(e) => {
                        if (/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  )
                }}
              />
              {touched.gender && !formData.gender && (
                <div className="field-error"><i className="fas fa-times"></i> This field is required.</div>
              )}
            </div>
          </div>

          {(() => {
            // Check if this is an event service
            const isEventService = selectedService?.category === 'event' || 
                                  (isEditMode && existingBooking?.data?.category === 'event');
            
            // Check if this is a single passenger service
            const maxPax = selectedBoat?.slots || selectedService?.minPax || 30;
            
            // Hide passenger container and actions only for event services with max pax 1
            // Keep visible for stay and experience services regardless of pax count
            const shouldHidePassengerSection = isEventService && maxPax === 1;
            
            // Show single passenger notice for non-event services with max pax 1
            if (maxPax === 1 && !isEventService) {
              return (
                <div className="form-section single-passenger-notice">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-user-check"></i>
                    </div>
                    <div className="section-title">
                      <h2>Passenger Information</h2>
                      <p>This service is designed for 1 passenger only. Your contact information above serves as the passenger details.</p>
                    </div>
                  </div>
                </div>
              );
            }

            // Hide entire section for event services with max pax 1
            if (shouldHidePassengerSection) {
              return null;
            }

            // Show full passenger section for all other cases
            return (
              <div className="form-section passengers-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="section-title">
                    <h2>Additional Passenger Information</h2>
                    <p>Add details for additional passengers (contact person above counts as passenger #1)
                      {(() => {
                        const minPax = selectedBoat?.slots || selectedService?.minPax;
                        const maxAdditional = (selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption))
                          ? (selectedBoat?.slots || 1) - 1
                          : (selectedService?.minPax || 30) - 1;

                        if (minPax && minPax > 1) {
                          return ` (minimum ${minPax - 1} additional passengers required, maximum ${maxAdditional})`;
                        } else {
                          return ` (maximum ${maxAdditional} additional passengers)`;
                        }
                      })()}
                    </p>
                  </div>
                </div>
            <div className="passenger-container">
              {passengers.map((p, i) => (
                <div className="passenger-item" key={i}>
                  <input
                    type="text"
                    value={p.name}
                    onChange={(e) => handlePassengerChange(i, 'name', e.target.value)}
                    placeholder="Passenger Full Name"
                    className={touched[`passenger_name_${i}`] && !p.name ? 'input-error passenger-name' : 'passenger-name'}
                    onBlur={() => handleBlur(`passenger_name_${i}`)}
                  />
                  {touched[`passenger_name_${i}`] && !p.name && (
                    <div className="field-error"><i className="fas fa-times"></i> Name is required.</div>
                  )}
                  <div className="passenger-details-row">
                    <Select
                      classNamePrefix="react-select"
                      options={genderOptions}
                      value={genderOptions.find(opt => opt.value === p.gender) || null}
                      onChange={opt => handlePassengerChange(i, 'gender', opt ? opt.value : '')}
                      placeholder="Gender"
                      onBlur={() => handleBlur(`passenger_gender_${i}`)}
                      styles={{ container: base => ({ ...base, minWidth: 120, marginRight: 8 }) }}
                      isSearchable
                      components={{
                        Input: (props) => (
                          <components.Input
                            {...props}
                            onKeyPress={(e) => {
                              if (/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        )
                      }}
                    />
                    <Select
                      classNamePrefix="react-select"
                      options={countryOptions}
                      value={countryOptions.find(opt => opt.value === p.country) || null}
                      onChange={opt => handlePassengerChange(i, 'country', opt ? opt.value : '')}
                      placeholder="Country"
                      isSearchable
                      onBlur={() => handleBlur(`passenger_country_${i}`)}
                      styles={{ container: base => ({ ...base, minWidth: 160, marginRight: 8 }) }}
                      components={{
                        Input: (props) => (
                          <components.Input
                            {...props}
                            onKeyPress={(e) => {
                              if (/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        )
                      }}
                    />
                    <Select
                      classNamePrefix="react-select"
                      options={ageOptions}
                      value={ageOptions.find(opt => opt.value === p.age) || null}
                      onChange={opt => handlePassengerChange(i, 'age', opt ? opt.value : '')}
                      placeholder="Age"
                      onBlur={() => handleBlur(`passenger_age_${i}`)}
                      styles={{ container: base => ({ ...base, minWidth: 80 }) }}
                      isSearchable
                      components={{
                        Input: (props) => (
                          <components.Input
                            {...props}
                            inputMode="numeric"
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                                e.preventDefault();
                              }
                            }}
                          />
                        )
                      }}
                    />
                    <button
                      className="remove-passenger"
                      onClick={() => handleRemovePassenger(i)}
                      disabled={passengers.length === 1}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  {/* Error messages for each field */}
                  {touched[`passenger_gender_${i}`] && !p.gender && (
                    <div className="field-error"><i className="fas fa-times"></i> Gender is required.</div>
                  )}
                  {touched[`passenger_country_${i}`] && !p.country && (
                    <div className="field-error"><i className="fas fa-times"></i> Country is required.</div>
                  )}
                  {touched[`passenger_age_${i}`] && !p.age && (
                    <div className="field-error"><i className="fas fa-times"></i> Age is required.</div>
                  )}
                </div>
              ))}
            </div>
            <div className="passenger-actions">
              <button
                className="add-passenger"
                onClick={handleAddPassenger}
                disabled={(() => {
                  const maxPax = (selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption))
                    ? (selectedBoat?.slots || 1)
                    : (selectedService?.minPax || 30);
                  
                  // If max pax is 1, disable button completely (contact person fills the slot)
                  if (maxPax === 1) return true;
                  
                  return passengers.length >= maxPax - 1;
                })()}
              >
                <i className="fas fa-plus-circle"></i>
                <span>Add Another Passenger</span>
                <small>
                  {(() => {
                    const maxPax = (selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption))
                      ? (selectedBoat?.slots || 1)
                      : (selectedService?.minPax || 30);
                    
                    if (maxPax === 1) {
                      return "(Contact person only)";
                    }
                    
                    return `(${passengers.length + 1}/${maxPax})`;
                  })()}
                </small>
              </button>
            </div>
              </div>
            );
          })()}

          <div className="form-section booking-details-section">
            <div className="section-header">
              <div className="section-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <div className="section-title">
                <h2>Finalize Your Booking</h2>
                <p>Choose your preferred date and confirm your reservation</p>
              </div>
            </div>

            <div className="form-group date-group">
              <label htmlFor="date">
                <i className="fas fa-calendar-alt"></i>
                Select Booking Date
              </label>
              <input
                type="date"
                id="date"
                value={formData.date}
                min={(() => {
                  const threeDaysFromNow = new Date();
                  threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
                  return threeDaysFromNow.toISOString().split('T')[0];
                })()}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="date-input"
                onClick={() => {
                  const input = document.getElementById('date');
                  if (input && 'showPicker' in input) {
                    try {
                      input.showPicker();
                    } catch (error) {
                      console.error('Error opening date picker:', error);
                    }
                  }
                }}
              />
              <div className="advance-booking-info">
                <i className="fas fa-clock"></i>
                <span>All bookings must be made at least 3 days in advance.</span>
              </div>
              {error && (
                <div className="error-banner">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{error}</span>
                </div>
              )}
              {formData.date && isEditMode && (
                <div className="selected-date-display">
                  <i className="fas fa-check-circle"></i>
                  <span>Selected: {formatDateForDisplay(formData.date)}</span>
                </div>
              )}
            </div>

            <div className="booking-summary">
              <div className="summary-row">
                <span>Total Passengers:</span>
                <span className="summary-value">{passengers.filter(p => p.name.trim()).length + 1}</span>
              </div>
              <div className="summary-row">
                <span>Selected Services:</span>
                <span className="summary-value">
                  {(selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption))
                    ? (selectedBoat?.name || 'Loading...')
                    : (selectedService?.title || 'Loading...')}
                </span>
              </div>
              {(selectedService?.category === 'experience' || selectedService?.category === 'event' ||
                (isEditMode && (existingBooking?.category === 'experience' || existingBooking?.category === 'event' || isExperienceBooking(getCurrentServiceTitle())))) && (
                  <div className="summary-row">
                    <span>Price per person:</span>
                    <span className="summary-value">PHP{
                      (selectedBoat?.id || (isEditMode && existingBooking?.data?.selectedOption))
                        ? (selectedBoat?.price || 0)
                        : (selectedService?.price || 0)
                    }</span>
                  </div>
                )}
              <div className="summary-row total-row">
                <span>Total Amount:</span>
                <span className="summary-value total-price">PHP{getTotalPrice()}</span>
              </div>

              <div className="terms-conditions-section">
                <label className="terms-checkbox-container">
                  <input
                    type="checkbox"
                    checked={formData.termsAccepted}
                    onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                    className="terms-checkbox"
                  />
                  <span className="checkmark"></span>
                  <span className="terms-text">
                    I agree to the <button 
                      type="button" 
                      className="terms-link" 
                      onClick={() => setShowTermsModal(true)}
                    >
                      Terms and Conditions
                    </button>
                  </span>
                </label>
              </div>

              {(serviceData?.selectedServiceId === 'fisherman-for-a-day' ||
                (isEditMode && getCurrentServiceTitle() === 'Fisherman for a Day')) && (
                  <div className="pricing-note">
                    <i className="fas fa-info-circle"></i>
                    <span>Pricing is per person. Total amount = {passengers.filter(p => p.name.trim()).length + 1} passengers × PHP{selectedBoat?.price || 0}</span>
                  </div>
                )}

              {/* Pay Button - Only show for new bookings */}
              {!isEditMode && (
                <div className="payment-button-section">
                  <button
                    className={`payment-btn-inline ${paymentCompleted ? 'payment-completed' : ''}`}
                    onClick={() => {
                      const isValid = validateBookingData();
                      if (!isValid) {
                        return;
                      }

                      const bookingData = {
                        bookingId: `BK-${Date.now()}`,
                        boat: selectedBoat?.name || (selectedService?.title || getCurrentServiceTitle()),
                        date: formData.date,
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone
                      };
                      setPendingBookingData(bookingData);
                      setShowPaymentModal(true);
                    }}
                    disabled={paymentCompleted}
                  >
                    <i className={paymentCompleted ? "fas fa-check-circle" : "fas fa-credit-card"}></i>
                    <span>{paymentCompleted ? "Payment Completed" : "Pay Now"}</span>
                  </button>
                </div>
              )}
            </div>

            <button
              className="btn-book enhanced-book-btn"
              onClick={handleBook}
              disabled={(!isEditMode && bookingCompleted) || isLoading || (!isEditMode && !paymentCompleted)} // Disabled until payment is completed
              title={(!isEditMode && bookingCompleted) ? 'Booking already completed' : (!isEditMode && !paymentCompleted) ? 'Payment must be completed successfully before confirming booking' : ''}
            >
              <i className={isLoading ? "fas fa-spinner fa-spin" : isEditMode ? "fas fa-edit" : bookingCompleted ? "fas fa-check" : ""}></i>
              <span>{isLoading ? 'Processing...' : isEditMode ? 'Review Update' : bookingCompleted ? 'Booking Completed' : 'Confirm Booking'}</span>
              <small>{isLoading ? 'Please wait while we process your request' : isEditMode ? 'Review changes before confirming' : bookingCompleted ? 'Use "Book Another Adventure" to make a new booking' : paymentCompleted ? 'Payment completed - ready to book!' : paymentFailed ? 'Payment failed - please try payment again' : 'Complete payment first to confirm booking'}</small>
            </button>

            {bookingCompleted && !showConfirmation && !isEditMode && (
              <div className="reopen-confirmation-section">
                <button
                  className="btn-reopen-confirmation"
                  onClick={() => setShowConfirmation(true)}
                >
                  <i className="fas fa-eye"></i>
                  <span>View Booking Confirmation</span>
                  <small>Click to see your booking details again</small>
                </button>
              </div>
            )}

          </div>
        </div>


        {/* Unified Service Selection Component - Shows options when service has them */}
        {(() => {
          const shouldShowOptions = selectedService && selectedService.options?.length > 0;
          return shouldShowOptions;
        })() && (
            <div className="boat-selection">
              <div className="boat-selection-header">
                <div className="selection-title">
                  <h2>Choose Your Option</h2>
                  <p>Select the perfect option for your adventure from our {getCurrentServiceTitle() || 'service'} offerings</p>
                </div>
              </div>
              <div className="boats-grid enhanced-boats-grid">
                {boats.map((boat) => (
                  <div
                    key={boat.id}
                    className={`boat-card enhanced-boat-card ${selectedBoat?.id === boat.id ? 'selected-boat' : ''}`}
                    onClick={() => {
                      setSelectedBoat(boat);
                      // Only reset passengers if switching to a boat with fewer slots (accounting for contact person)
                      if (passengers.length > boat.slots - 1) {
                        setPassengers(passengers.slice(0, boat.slots - 1));
                      } else if (passengers.length === 0) {
                        setPassengers([{ name: '', gender: '', country: '', age: '' }]);
                      }
                      setShowConfirmation(false);
                      setError('');
                    }}
                  >
                    <div className="boat-image enhanced-boat-image">
                      <div className="boat-icon-container">
                        <img src={hiveroomsBooking} alt="Hive Rooms Booking" className="boat-icon" />
                      </div>
                      <div className="boat-overlay">
                        <div className="boat-price-tag">
                          <span className="price-currency">PHP</span>
                          <span className="price-amount">{boat.price}</span>
                        </div>
                      </div>
                    </div>

                    <div className="boat-details enhanced-boat-details">
                      <div className="boat-header">
                        <h3>{boat.name}</h3>
                        <div className="boat-capacity">
                          <i className="fas fa-users"></i>
                          <span>{boat.slots} max</span>
                        </div>
                      </div>

                      <p className="boat-description">{boat.description}</p>

                      <div className="boat-features enhanced-features">
                        {boat.features.map((f, i) => (
                          <div className="feature enhanced-feature" key={i}>
                            <span>{typeof f === 'string' ? f : f.label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="boat-action">
                        <button className="select-boat-btn">
                          {selectedBoat?.id === boat.id ? (
                            <>
                              <i className="fas fa-check"></i>
                              <span>Selected</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-plus"></i>
                              <span>Select This Option</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Experience services show only contact form - no additional sections */}
        {false && selectedService?.category === 'experience' && (
          <div className="experience-package-section">
            <div className="experience-header">
              <div className="section-title">
                <h2>Experience Package Details</h2>
                <p>This is a fixed experience package with all inclusions</p>
              </div>
            </div>
            <div className="experience-card">
              <div className="experience-image">
                <img src={selectedService.image} alt={selectedService.title} />
              </div>
              <div className="experience-content">
                <h3>{selectedService.title}</h3>
                <p className="experience-description">{selectedService.description}</p>

                <div className="experience-details">
                  <div className="experience-stats">
                    <div className="stat-item">
                      <i className="fas fa-star"></i>
                      <span>{selectedService.rating} ({selectedService.reviews} reviews)</span>
                    </div>
                    <div className="stat-item">
                      <i className="fas fa-users"></i>
                      <span>Flexible group size</span>
                    </div>
                    <div className="stat-item">
                      <i className="fas fa-tag"></i>
                      <span>Fixed Package Price</span>
                    </div>
                  </div>

                  {selectedService.features && selectedService.features.length > 0 && (
                    <div className="experience-features">
                      <h4>What's Included:</h4>
                      <ul>
                        {selectedService.features.map((feature, index) => (
                          <li key={index}>
                            <i className="fas fa-check"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="experience-pricing">
                  <div className="price-display">
                    <span className="price-label">Total Package Price</span>
                    <span className="price-amount">₱{selectedService.price.toLocaleString()}</span>
                  </div>
                  {selectedService.recommended && (
                    <div className="recommended-badge">
                      <i className="fas fa-thumbs-up"></i>
                      <span>Recommended</span>
                    </div>
                  )}
                  {selectedService.bestPrice && (
                    <div className="best-price-badge">
                      <i className="fas fa-fire"></i>
                      <span>Best Price</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showConfirmation && (
        <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="confirmation enhanced-confirmation confirmation-modal" id="confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="confirmation-title">
                <h2>Booking Request Received!</h2>
                <p>Your boat adventure request has been successfully submitted</p>
              </div>
            </div>

            <div className="confirmation-details enhanced-confirmation-details">
              <div className="detail-card booking-id-card">
                <div className="card-header">
                  <i className="fas fa-ticket-alt"></i>
                  <h3>Booking Reference</h3>
                </div>
                <div className="detail-content">
                  <div className="detail-row booking-id-row">
                    <span className="detail-label">Booking ID:</span>
                    <div className="booking-id-container">
                      <span className="detail-value booking-id-value">{bookingId}</span>
                      <button
                        className="copy-btn"
                        onClick={handleCopyBookingId}
                        title="Copy Booking ID"
                      >
                        <i className="fas fa-copy"></i>
                        {copyFeedback && <span className="copy-feedback">{copyFeedback}</span>}
                      </button>
                    </div>
                  </div>
                  <p className="booking-id-note">
                    <i className="fas fa-info-circle"></i>
                    Save this ID to track your booking and use it for check-in
                  </p>
                </div>
              </div>

              <div className="detail-card service-card">
                <div className="card-header">
                  <i className="fas fa-ship"></i>
                  <h3>Your Booking Details</h3>
                </div>
                <div className="detail-content">
                  {getCurrentServiceTitle() && (
                    <div className="detail-row">
                      <span className="detail-label">Service Provider:</span>
                      <span className="detail-value">{getCurrentServiceTitle()}</span>
                    </div>
                  )}
                  {!(isExperienceService(serviceData?.selectedServiceId) ||
                    (isEditMode && (existingBooking?.category === 'experience' || selectedService?.category === 'experience' || isExperienceBooking(getCurrentServiceTitle())))) && (
                      <div className="detail-row">
                        <span className="detail-label">Selected Boat:</span>
                        <span className="detail-value">{selectedBoat?.name || 'Loading...'}</span>
                      </div>
                    )}
                  <div className="detail-row">
                    <span className="detail-label">Date & Time:</span>
                    <span className="detail-value">{new Date(formData.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                  </div>
                  {!(isExperienceService(serviceData?.selectedServiceId) ||
                    (isEditMode && (existingBooking?.category === 'experience' || selectedService?.category === 'experience' || isExperienceBooking(getCurrentServiceTitle())))) && (
                      <div className="detail-row">
                        <span className="detail-label">Boat Capacity:</span>
                        <span className="detail-value">Up to {selectedBoat?.slots || 1} passengers</span>
                      </div>
                    )}
                  <div className="detail-row total-row">
                    <span className="detail-label">Total Amount:</span>
                    <span className="detail-value total-amount">PHP{getTotalPrice()}</span>
                  </div>
                </div>
              </div>

              <div className="detail-card contact-card">
                <div className="card-header">
                  <i className="fas fa-user"></i>
                  <h3>Contact Information</h3>
                </div>
                <div className="detail-content">
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{formData.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{formData.email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{formData.phone}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Age:</span>
                    <span className="detail-value">{formData.age}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Country:</span>
                    <span className="detail-value">{formData.country}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Gender:</span>
                    <span className="detail-value">{formData.gender}</span>
                  </div>
                </div>
              </div>

              <div className="detail-card passengers-card">
                <div className="card-header">
                  <i className="fas fa-users"></i>
                  <h3>Passengers ({passengers.filter(p => p.name.trim() !== '').length + 1})</h3>
                </div>
                <div className="detail-content">
                  <div className="passenger-list enhanced-passenger-list compact-passenger-list">
                    <div className="passenger-item-confirm compact-passenger-item">
                      <div className="passenger-info-compact">
                        <span className="passenger-name-compact">{formData.name}</span>
                        <span className="passenger-role-badge">Contact</span>
                        <span className="passenger-details-compact">{formData.gender}, {formData.country}, {formData.age}y</span>
                      </div>
                    </div>
                    {passengers.filter(p => p.name.trim() !== '').map((p, i) => (
                      <div key={i} className="passenger-item-confirm compact-passenger-item">
                        <div className="passenger-info-compact">
                          <span className="passenger-name-compact">{p.name}</span>
                          <span className="passenger-details-compact">{p.gender}, {p.country}, {p.age}y</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* <div className="detail-card features-card">
              <div className="card-header">
                <i className="fas fa-star"></i>
                <h3>Included Features</h3>
              </div>
              <div className="detail-content">
                <div className="feature-list enhanced-feature-list">
                  {(selectedBoat?.features || []).map((feature, i) => (
                    <div key={i} className="feature-item-confirm">
                      <i className={`fas ${feature.icon}`}></i>
                      <span>{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div> */}
            </div>

            <div className="confirmation-actions enhanced-confirmation-actions">
              {isViewingPrintFromModal && (
                <button className="btn-print" onClick={handleBackToModal}>
                  <i className="fas fa-arrow-left"></i>
                  <span>Back to Options</span>
                </button>
              )}
              {!isEditMode && !isViewingPrintFromModal && (
                <button className="btn-new-booking" onClick={handleNewBooking}>
                  <i className="fas fa-plus-circle"></i>
                  <span>Book Another Adventure</span>
                </button>
              )}
              <button className="btn-print" onClick={() => window.print()}>
                <i className="fas fa-print"></i>
                <span>Print Confirmation</span>
              </button>
            </div>

            {/* <div className="confirmation-footer">
            <p>
              <i className="fas fa-info-circle"></i>
              A confirmation email has been sent to {formData.email}. Please arrive 15 minutes before your scheduled departure.
            </p>
          </div> */}
          </div>
        </div>
      )}

      {/* Success Modal for Edit Mode */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-content">
            <div className="success-modal-header">
              <div className="success-icon-large">
                <i className={isBookingUpdated ? "fas fa-check-circle" : "fas fa-exclamation-triangle"}></i>
              </div>
              <h2>{isBookingUpdated ? 'Booking Updated Successfully!' : 'Confirm Booking Update'}</h2>
              <p>{isBookingUpdated ? 'Your booking has been updated with the new details' : 'Please review your changes and confirm to update your booking'}</p>
            </div>

            <div className="success-modal-info">
              <div className="booking-id-display">
                <span className="booking-id-label">Booking ID</span>
                <div className="booking-id-container">
                  <span className="booking-id-value">{bookingId}</span>
                  <button
                    className="copy-btn modal-copy-btn"
                    onClick={handleCopyBookingId}
                    title="Copy Booking ID"
                  >
                    <i className="fas fa-copy"></i>
                    {copyFeedback && <span className="copy-feedback">{copyFeedback}</span>}
                  </button>
                </div>
              </div>
              {/* <div className="success-note">
                <i className="fas fa-envelope"></i>
                <span>Confirmation email has been sent with updated details</span>
              </div> */}
            </div>

            <div className="success-modal-actions">
              {!isBookingUpdated ? (
                <>
                  <button
                    className="success-action-btn primary"
                    onClick={handleConfirmUpdate}
                    disabled={isLoading}
                  >
                    <i className={isLoading ? "fas fa-spinner fa-spin" : "fas fa-check"}></i>
                    <span>{isLoading ? 'Updating...' : 'Confirm Update'}</span>
                    <small>{isLoading ? 'Please wait' : 'Save changes to booking'}</small>
                  </button>

                  <button className="success-action-btn secondary" onClick={handleMakeMoreChanges}>
                    <i className="fas fa-edit"></i>
                    <span>Make More Changes</span>
                    <small>Continue editing</small>
                  </button>

                  <button className="success-action-btn tertiary" onClick={() => setShowSuccessModal(false)}>
                    <i className="fas fa-times"></i>
                    <span>Cancel Update</span>
                    <small>Return without saving</small>
                  </button>
                </>
              ) : (
                <>
                  <button className="success-action-btn primary" onClick={handleViewDetails}>
                    <i className="fas fa-print"></i>
                    <span>View Print</span>
                    <small>See your changes</small>
                  </button>

                  <button className="success-action-btn secondary" onClick={handleMakeMoreChanges}>
                    <i className="fas fa-edit"></i>
                    <span>Make More Changes</span>
                    <small>Continue editing</small>
                  </button>

                  <button className="success-action-btn tertiary" onClick={handleReturnHome}>
                    <i className="fas fa-home"></i>
                    <span>Return to Home</span>
                    <small>Exit booking</small>
                  </button>
                </>
              )}
            </div>

            <div className="success-modal-footer">
              <p>
                <i className="fas fa-info-circle"></i>
                You will be automatically logged out after selecting an option for security
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        bookingData={pendingBookingData}
        totalAmount={getTotalPrice()}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentFailure={handlePaymentFailure}
        onPaymentError={handlePaymentError}
      />

      {/* Terms and Conditions Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
}